/**
 * User authentication service handler.
 *
 * Uses OAuth2 Authorization Code + PKCE flow (RFC 6749 / RFC 7636 / RFC 8252).
 *
 * Each platform (web, desktop, mobile) uses its own registered OAuth client
 * with platform-appropriate redirect URIs:
 *
 * - **Web**:    redirects the browser tab; callback via `?code=&state=` URL params.
 * - **Desktop** (Tauri): navigates the WebView; callback via same mechanism.
 * - **Mobile**  (Capacitor): uses the app WebView so the authenticated server
 *   session remains available during authorization.
 *
 * The `platform` adapter supplies the correct `clientId` and `redirectUri` at
 * runtime so no build-time branching is needed.
 *
 * ### Navigation
 * `setNavigate` must be called once with the React Router `navigate`
 * function before logout can redirect to the login screen.
 */
import { getMethod, postMethod } from "./rest-handler";
import { store } from "@store/index";
import { authReducer, loginReducer } from "@store/auth-slice";
import {
  getRefreshToken,
  removeAccessToken,
  removeRefreshToken,
  saveAmibrokerEnabled,
  setAccessToken,
  setRefreshToken,
} from "./local-storage-handler";
import { disconnectWS } from "./websocket-handler";
import { NavigateFunction } from "react-router-dom";
import { platform } from "@platform";
import { machineIdReducer } from "@store/machine-id-slice";
import { setAmibrokerEnabled, setAmibrokerInitialized } from "@store/amibroker-slice";
import {
  generateCodeVerifier,
  generateCodeChallenge,
  storeVerifier,
  retrieveVerifier,
} from "./pkce";

const PKCE_STATE_KEY = "_pkce_state";
const PKCE_VERIFIER_KEY = "_pkce_verifier";

let navigate: NavigateFunction;

const clearClientAuthState = () => {
  removeAccessToken();
  removeRefreshToken();
  localStorage.removeItem("auto_login");
  localStorage.removeItem(PKCE_STATE_KEY);
  localStorage.removeItem(PKCE_VERIFIER_KEY);
  localStorage.removeItem("_force_login_prompt");
};

/**
 * Store the React Router `navigate` function for use by `refreshAccessToken`
 * when it needs to redirect to `/` after a fatal token failure.
 *
 * @param nav - The `NavigateFunction` returned by `useNavigate()`.
 */
export const setNavigate = (nav) => {
  navigate = nav;
};

async function ensureDeviceId(): Promise<string> {
  const existing = store.getState().machineId.machineId;
  if (existing) return existing;

  try {
    const generated = await platform.getDeviceId();
    if (generated) {
      store.dispatch(machineIdReducer({ machineId: generated }));
      return generated;
    }
  } catch {
    // Ignore and fall through to a deterministic placeholder.
  }

  return "unknown-device";
}

/**
 * Authenticate in the app UI and start OAuth authorization.
 *
 * Builds the PKCE code challenge, authenticates the server session, and
 * requests an authorization code without navigating away from the app.
 */
export const login = async (username: string, password: string): Promise<{ code: string; state: string }> => {
  const authServerBaseUrl = process.env.REACT_APP_MARKETVIEW_URL;

  // Use the platform-specific client ID and redirect URI so the authorization
  // request matches the server-side registration for this runtime env.
  const clientId = platform.getOAuthClientId();
  const redirectUri = platform.getOAuthRedirectUri();
  const scopes = process.env.REACT_APP_OAUTH_SCOPES || "read write";

  if (!clientId || !redirectUri) {
    throw new Error(
      "OAuth configuration incomplete. Ensure per-platform client IDs and redirect URIs are set."
    );
  }

  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  const state = Math.random().toString(36).slice(2) + Date.now().toString(36);

  storeVerifier(verifier);
  localStorage.setItem(PKCE_STATE_KEY, state);

  const authorizeParams = new URLSearchParams({
    response_type: "code",
    client_id: clientId,
    redirect_uri: redirectUri,
    scope: scopes,
    code_challenge: challenge,
    code_challenge_method: "S256",
    state,
  });

  // Always use the configured auth server URL directly — never rely on a Vite
  // proxy, which breaks the browser redirect + cookie model.
  if (!authServerBaseUrl) {
    throw new Error(
      "REACT_APP_MARKETVIEW_URL is not set. Configure the backend auth server URL."
    );
  }

  const authUrl = `${authServerBaseUrl}/oauth/authorize?${authorizeParams.toString()}`;

  await postMethod("/auth/session", JSON.stringify({ username, password }));
  const response: any = await getMethod(authUrl);
  return response.data;
};

/**
 * Complete the OAuth authorization code exchange.
 *
 * Called after the auth server redirects back with `?code=&state=` params
 * (web/desktop) or fires the `appUrlOpen` deep-link event (mobile).
 */
export const exchangeAuthorizationCode = async (code: string | null, state: string | null) => {
  const verifier = retrieveVerifier();
  const expectedState = localStorage.getItem(PKCE_STATE_KEY);

  // Use the same platform-specific client ID and redirect URI that were used
  // when building the initial authorization request.
  const clientId = platform.getOAuthClientId();
  const redirectUri = platform.getOAuthRedirectUri();

  if (!clientId || !redirectUri) {
    throw new Error(
      "OAuth configuration incomplete. Ensure per-platform client IDs and redirect URIs are set."
    );
  }

  if (!code || !state || !verifier || expectedState !== state) {
    throw new Error("Invalid authorization response (missing/invalid code, verifier or state)");
  }

  const deviceId: string = await ensureDeviceId();
  const params = new URLSearchParams();
  params.append("grant_type", "authorization_code");
  params.append("code", code);
  params.append("client_id", clientId);
  params.append("redirect_uri", redirectUri);
  params.append("code_verifier", verifier);
  params.append("deviceId", deviceId);

  const headers = {
    "Content-Type": "application/x-www-form-urlencoded",
  };

  const response = await postMethod("/oauth/token", params.toString(), headers);
  const data = response["data"];

  setAccessToken(data.access_token);
  setRefreshToken(data.refresh_token);
  store.dispatch(authReducer({ isAuthenticated: true }));

  localStorage.removeItem(PKCE_STATE_KEY);

  try {
    const accountResponse = await getMethod("account/details");
    const accountData = accountResponse["data"];
    store.dispatch(loginReducer(accountData));
  } catch {
    // account/details may not exist in all deployments; metadata fetch still works.
  }
};

export const refreshAccessToken = async () => {
  const url: string = "/oauth/token";
  const refreshToken: string = getRefreshToken();

  // Use the platform-specific client ID so the refresh request is sent under
  // the same client registration as the original token grant.
  const clientId = platform.getOAuthClientId();
  const deviceId = await ensureDeviceId();

  if (!clientId) {
    throw new Error("Missing OAuth client ID for refresh token flow");
  }

  const params = new URLSearchParams();
  params.append("client_id", clientId);
  params.append("grant_type", "refresh_token");
  params.append("refresh_token", refreshToken);
  params.append("deviceId", deviceId);

  const headers = {
    "Content-Type": "application/x-www-form-urlencoded",
  };

  try {
    const response = await postMethod(url, params.toString(), headers);
    const data = response["data"];
    setAccessToken(data.access_token);
    setRefreshToken(data.refresh_token);
    store.dispatch(authReducer({ isAuthenticated: true }));
  } catch (error) {
    clearClientAuthState();
    disconnectWS();
    store.dispatch({ type: "LOGOUT" });
    if (navigate) {
      navigate("/");
    }
    throw error;
  }
};

export const logout = async () => {
  // Always notify desktop runtime to kill/cleanup AmiBroker on logout.
  import("./ipc-handler")
    .then(({ notifyAmibrokerLogout }) => notifyAmibrokerLogout())
    .catch(() => {
      // Non-blocking integration cleanup.
    });

  store.dispatch(setAmibrokerEnabled(false));
  store.dispatch(setAmibrokerInitialized(false));
  saveAmibrokerEnabled(false);
  disconnectWS();
  store.dispatch({ type: "LOGOUT" });

  const deviceId: string = store.getState().machineId.machineId || "unknown-device";
  const url: string = "/oauth/logout";

  // Use the platform-specific client ID so the backend can locate and
  // invalidate the correct session / refresh token.
  const clientId: string = platform.getOAuthClientId();
  const body: string = JSON.stringify({ clientId, deviceId });

  try {
    await postMethod(url, body);
  } catch (error) {
    // Local sign-out must still succeed if server-side revocation fails.
    console.warn("Logout API failed, completed local sign-out:", error);
  } finally {
    clearClientAuthState();

    if (navigate) {
      navigate("/", { replace: true });
    }
  }
};
